import posthog from 'posthog-js';
import type { PostHogConfig } from 'posthog-js';

/**
 * PostHog Analytics Configuration
 * Initializes PostHog for product analytics tracking
 */

let posthogInitialized = false;

const isPostHogExplicitlyDisabled = (): boolean =>
  String(import.meta.env.VITE_POSTHOG_DISABLED ?? '').toLowerCase() === 'true';

/**
 * Initialize PostHog with API key from environment variables
 * Fails gracefully if API key is not provided
 */
export const initPostHog = (): void => {
  if (isPostHogExplicitlyDisabled()) {
    if (import.meta.env.DEV) {
      console.warn('PostHog disabled via VITE_POSTHOG_DISABLED.');
    }
    return;
  }

  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

  // Check if key is missing or empty
  if (!posthogKey || posthogKey.trim() === '') {
    if (import.meta.env.DEV) {
      console.warn('PostHog API key not found. Analytics tracking disabled.');
    }
    return;
  }

  // Validate API key format (PostHog keys typically start with 'phx_')
  if (!posthogKey.startsWith('phx_')) {
    if (import.meta.env.DEV) {
      console.warn('PostHog API key appears invalid. Analytics tracking disabled.');
    }
    return;
  }

  const sessionReplayEnabled =
    String(import.meta.env.VITE_POSTHOG_SESSION_REPLAY ?? '').toLowerCase() === 'true';

  const privacySessionRecording: Partial<PostHogConfig> = sessionReplayEnabled
    ? {
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
        },
      }
    : {
        disable_session_recording: true,
      };

  try {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      // Ensure default feature flags initialization date
      defaults: '2025-05-24',
      // Capture exceptions using Error Tracking
      capture_exceptions: true,
      // Enable debug mode in development
      debug: import.meta.env.DEV,
      loaded: () => {
        if (import.meta.env.DEV) {
          console.log('PostHog initialized successfully');
        }
        posthogInitialized = true;
      },
      capture_pageview: true,
      capture_pageleave: true,
      // Enable autocapture for web (clicks, form submissions, etc.)
      autocapture: true,
      // Heatmaps / dead-clicks capture more surface area; keep off unless you enable replay.
      enable_heatmaps: sessionReplayEnabled,
      capture_dead_clicks: sessionReplayEnabled,
      // Enable web vitals autocapture (CLS, FCP, LCP, INP)
      capture_performance: {
        web_vitals_allowed_metrics: ['CLS', 'FCP', 'LCP', 'INP'],
      },
      persistence: 'localStorage+cookie',
      ...privacySessionRecording,
    });
  } catch (error) {
    console.error('Failed to initialize PostHog:', error);
    posthogInitialized = false;
  }
};

/**
 * Check if PostHog is initialized
 */
export const isPostHogInitialized = (): boolean => {
  return posthogInitialized;
};

/**
 * Track a custom event
 * @param eventName - Name of the event
 * @param properties - Optional properties to attach to the event
 */
export const trackEvent = (eventName: string, properties?: Record<string, unknown>): void => {
  if (!posthogInitialized) return;
  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

/**
 * Identify a user
 * @param distinctId - Unique identifier for the user
 * @param properties - Optional properties to attach to the user
 */
export const identifyUser = (distinctId: string, properties?: Record<string, unknown>): void => {
  if (!posthogInitialized) return;
  try {
    posthog.identify(distinctId, properties);
  } catch (error) {
    console.error('Failed to identify user:', error);
  }
};

/**
 * Reset user identification (for logout)
 */
export const resetUser = (): void => {
  if (!posthogInitialized) return;
  try {
    posthog.reset();
  } catch (error) {
    console.error('Failed to reset user:', error);
  }
};

/**
 * Set user properties
 * @param properties - Properties to set for the current user
 */
export const setUserProperties = (properties: Record<string, unknown>): void => {
  if (!posthogInitialized) return;
  try {
    posthog.setPersonProperties(properties);
  } catch (error) {
    console.error('Failed to set user properties:', error);
  }
};

export default posthog;
